import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ReconTransactionTable from '../../../components/tables/ReconTransactionTable';
import Button from '../../../components/common/Button';
import { useReconTransactionApi } from '../../../hooks/useReconTransactionApi';
import { useFileApi } from '../../../hooks/useFileApi';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { showAlert } from '../../../utils/sweetAlertUtils';
import { InvoiceDTO, ReconTransactionDTO } from '../../../services/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';
import Drawer from '../../../components/common/Drawer';
import FileUpload from '../../../components/common/FileUpload';
import FormField from '../../../components/common/FormField';
import ViewInvoiceHistory from '../../../components/drawer/ViewInvoiceHistory';
import ViewMemberBatches from '../../../components/drawer/ViewMemberBatches';
import { InvoiceTypeConstants } from '../../../constants/InvoiceTypeConstants';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { ReconTypeConstants } from '../../../constants/ReconTypeConstants';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const ReconTransactionList: React.FC = () => {
  const { search, extract, setIsUnallocated, setMemberId, setComponentId, setManuallyIgnored, isLoading } = useReconTransactionApi();
  const { uploadFile } = useFileApi();
  const { search: searchMembers, isLoading: isLoadingMembers } = useMemberApi();
  const [items, setItems] = useState<ReconTransactionDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [sortBy, setSortBy] = useState<string>('CreatedUtc');
  const [sortDescending, setSortDescending] = useState<boolean>(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAssignMemberDrawerOpen, setIsAssignMemberDrawerOpen] = useState(false);
  const [isInvoiceHistoryDrawerOpen, setIsInvoiceHistoryDrawerOpen] = useState(false);
  const [isMemberBatchesDrawerOpen, setIsMemberBatchesDrawerOpen] = useState(false);
  const [selectedReconTransaction, setSelectedReconTransaction] = useState<ReconTransactionDTO | null>(null);
  const [invoiceHistoryMemberId, setInvoiceHistoryMemberId] = useState<string>('');
  const [memberBatchesMemberId, setMemberBatchesMemberId] = useState<string>('');
  const [memberBatchesIdPassportNumber, setMemberBatchesIdPassportNumber] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [memberOptions, setMemberOptions] = useState<Array<{ value: string; label: string }>>([
    { value: '', label: '(Clear member)' },
  ]);
  const [originalMemberOption, setOriginalMemberOption] = useState<{ value: string; label: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  type ReconView = 'transactions' | 'confirmations' | 'unallocated';

  const viewFromUrl: ReconView = location.pathname.includes('/admin/recontransactions/confirmations')
    ? 'confirmations'
    : location.pathname.includes('/admin/recontransactions/unallocated')
      ? 'unallocated'
      : 'transactions';

  const getFiltersFromView = (view: ReconView) => {
    const mainReconTypeIds = [
      ReconTypeConstants.FULL_MATCH,
      ReconTypeConstants.MANUALLY_MATCHED,
      ReconTypeConstants.MANUALLY_IGNORED,
      ReconTypeConstants.PAYMENT_LINK_MATCHED,
      ReconTypeConstants.AUTOMATICALLY_IGNORED
    ];

    const allReconTypeIds = Object.values(ReconTypeConstants);

    switch (view) {
      case 'confirmations':
        return {
          isUnallocated: false,
          excludeReconTypeIds: mainReconTypeIds,
        };
      case 'unallocated':
        return { isUnallocated: true };
      default:
        return {
          isUnallocated: false,
          excludeReconTypeIds: allReconTypeIds.filter((id) => !mainReconTypeIds.includes(id)),
        };
    }
  };

  const viewLabel =
    viewFromUrl === 'confirmations'
      ? 'Confirmations'
      : viewFromUrl === 'unallocated'
        ? 'Unallocated'
        : 'Transactions';

  const activeMenuItem =
    viewFromUrl === 'confirmations'
      ? '/admin/recontransactions/confirmations'
      : viewFromUrl === 'unallocated'
        ? '/admin/recontransactions/unallocated'
        : '/admin/recontransactions';
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Recons', href: '#' },
    { label: viewLabel, href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters?: any) => {
    try {
      const response = await search({
        filters: { ...getFiltersFromView(viewFromUrl), ...filters },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Recon Transactions:', error);
      return 0;
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(sortKey);
      setSortDescending(true);
    }
    setCurrentPage(1);
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchItems(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFromUrl, sortBy, sortDescending]);

  useEffect(() => {
    fetchItems(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortBy, sortDescending]);

  const handleViewMember = (idPassportNumber: string) => {
    navigate(`/admin/members?idPassportNumber=${encodeURIComponent(idPassportNumber)}`);
  };

  const handleEditMember = (memberId: string) => {
    navigate(`/admin/members/${memberId}`);
  };

  const handleCreateInvoice = (transaction: ReconTransactionDTO) => {
    const transactionDate = transaction.transactionDate
      ? new Date(transaction.transactionDate)
      : new Date();

    const paidDate = isNaN(transactionDate.getTime())
      ? ''
      : transactionDate.toISOString().slice(0, 10);

    const year = transactionDate.getFullYear();
    const month = (transactionDate.getMonth() + 1).toString().padStart(2, '0');
    const yearMonth = `${year}${month}`;

    const prefilledInvoice: InvoiceDTO = {
      memberId: transaction.memberId,
      // InvoiceForm uses member.firstName/lastName/idPassportNumber only to build the initial dropdown label.
      // Recon transactions have a MemberSimpleDTO, so we map just the needed bits.
      member: transaction.member
        ? (({
          firstName: transaction.member.memberFirstName,
          lastName: transaction.member.memberLastName,
          idPassportNumber: transaction.member.idPassportNumber,
        } as unknown) as any)
        : undefined,
      invoiceTypeId: InvoiceTypeConstants.INVOICE_ONCE_OFF,
      adminComponentId: ComponentConstants.MEMBERS,
      yearMonth,
      year,
      month: Number(month),
      paidDate: (paidDate as unknown) as any,
      totalPaid: transaction.amount ?? 0,
      notes: transaction.id ? `Created from Recon Transaction: ${transaction.id}` : undefined,
      invoiceLineItems: [
        {
          description: transaction.description || 'Recon transaction payment',
          quantity: 1,
          priceIncl: transaction.amount ?? 0,
        },
      ],
    };

    navigate('/admin/invoices/create', {
      state: {
        prefilledInvoice,
      },
    });
  };

  const handleOpenInvoiceHistoryDrawer = async (transaction: ReconTransactionDTO) => {
    const memberId = (transaction.memberId ?? '').trim();
    if (!memberId) {
      await showAlert({
        title: 'No member linked',
        text: 'Invoice History is only available when the transaction has a member.',
        icon: 'warning',
      });
      return;
    }

    setInvoiceHistoryMemberId(memberId);
    setIsInvoiceHistoryDrawerOpen(true);
  };

  const handleCloseInvoiceHistoryDrawer = () => {
    setIsInvoiceHistoryDrawerOpen(false);
    setInvoiceHistoryMemberId('');
  };

  const handleOpenMemberBatchesDrawer = async (transaction: ReconTransactionDTO) => {
    const memberId = (transaction.memberId ?? '').trim();
    if (!memberId) {
      await showAlert({
        title: 'No member linked',
        text: 'Member Batches is only available when the transaction has a member.',
        icon: 'warning',
      });
      return;
    }

    setMemberBatchesMemberId(memberId);
    setMemberBatchesIdPassportNumber((transaction.member?.idPassportNumber ?? '').trim());
    setIsMemberBatchesDrawerOpen(true);
  };

  const handleCloseMemberBatchesDrawer = () => {
    setIsMemberBatchesDrawerOpen(false);
    setMemberBatchesMemberId('');
    setMemberBatchesIdPassportNumber('');
  };

  const handleSetIsUnallocated = async (transactionId: string, isUnallocated: boolean) => {
    try {
      await setIsUnallocated(transactionId, isUnallocated);
      
      // Start refresh immediately, don't wait for alert dismissal
      await refreshAfterMutation();
      
      showAlert({
        title: 'Updated',
        text: isUnallocated ? 'Transaction moved to Unallocated.' : 'Allocation reset.',
        icon: 'success',
      });
    } catch (error) {
      console.error('Error updating IsUnallocated:', error);
      await showAlert({
        title: 'Update failed',
        text: 'Something went wrong updating the transaction. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleSetComponentId = async (transactionId: string, componentId: string) => {
    try {
      await setComponentId(transactionId, componentId);
      
      // Start refresh immediately, don't wait for alert dismissal
      await refreshAfterMutation();
      
      showAlert({
        title: 'Updated',
        text: 'Component confirmed.',
        icon: 'success',
      });
    } catch (error) {
      console.error('Error updating ComponentId:', error);
      await showAlert({
        title: 'Update failed',
        text: 'Something went wrong updating the component. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleIgnoreTransaction = async (transactionId: string) => {
    try {
      await setManuallyIgnored(transactionId);
      
      // Start refresh immediately, don't wait for alert dismissal
      await refreshAfterMutation();
      
      showAlert({
        title: 'Ignored',
        text: 'Transaction ignored.',
        icon: 'success',
      });
    } catch (error) {
      console.error('Error ignoring recon transaction:', error);
      await showAlert({
        title: 'Ignore failed',
        text: 'Something went wrong ignoring the transaction. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleOpenAssignMemberDrawer = (transaction: ReconTransactionDTO) => {
    setSelectedReconTransaction(transaction);

    const existingMemberId = (transaction.memberId ?? '').trim();
    const existingMemberLabel = transaction.member
      ? `${transaction.member.memberFirstName ?? ''} ${transaction.member.memberLastName ?? ''} (${transaction.member.idPassportNumber ?? ''})`.trim()
      : (existingMemberId ? `Existing member (${existingMemberId})` : '');

    const clearOption = { value: '', label: '(Clear member)' };

    if (existingMemberId && existingMemberLabel) {
      const existingOption = { value: existingMemberId, label: existingMemberLabel };
      setOriginalMemberOption(existingOption);
      setMemberOptions([clearOption, existingOption]);
      setSelectedMemberId(existingMemberId);
    } else {
      setOriginalMemberOption(null);
      setMemberOptions([clearOption]);
      setSelectedMemberId('');
    }

    setIsAssignMemberDrawerOpen(true);
  };

  const handleCloseAssignMemberDrawer = () => {
    setIsAssignMemberDrawerOpen(false);
    setSelectedReconTransaction(null);
    setSelectedMemberId('');
    setMemberOptions([{ value: '', label: '(Clear member)' }]);
    setOriginalMemberOption(null);
  };

  const handleMemberSearch = async (query: string) => {
    const response = await searchMembers({
      filters: {
        generalSearchTerm: query,
        isDeleted: false,
      },
      page: 1,
      pageSize: 20,
      sortBy: 'firstName',
    });

    const searchResults =
      response?.data?.results?.map((member: any) => ({
        value: member.id ?? '',
        label: `${member.firstName ?? ''} ${member.lastName ?? ''} (${member.idPassportNumber ?? ''})`,
      })) || [];

    const clearOption = { value: '', label: '(Clear member)' };
    const baseOptions = originalMemberOption
      ? [originalMemberOption, ...searchResults.filter((opt) => opt.value !== originalMemberOption.value)]
      : searchResults;

    setMemberOptions([clearOption, ...baseOptions.filter((opt) => opt.value)]);
  };

  const handleAssignMemberChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSelectedMemberId((e.target.value as string) || '');
  };

  const handleSaveAssignedMember = async () => {
    const reconTransactionId = (selectedReconTransaction?.id ?? '').trim();
    if (!reconTransactionId) return;

    try {
      await setMemberId(reconTransactionId, selectedMemberId || undefined);
      handleCloseAssignMemberDrawer();

      // Start refresh immediately, don't wait for alert dismissal
      await refreshAfterMutation();

      showAlert({
        title: 'Updated',
        text: selectedMemberId ? 'Member assigned.' : 'Member cleared.',
        icon: 'success',
      });
    } catch (error) {
      console.error('Error assigning recon transaction member:', error);
      await showAlert({
        title: 'Update failed',
        text: 'Something went wrong updating the member. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: getFiltersFromView(viewFromUrl),
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Recon Transactions:', error);
    }
  };

  const handleOpenDrawer = () => {
    setSelectedFile(null);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      await showAlert({
        title: 'No file selected',
        text: 'Please choose a CSV file first.',
        icon: 'warning',
      });
      return;
    }

    setIsUploading(true);
    try {
      const uploadResponse = await uploadFile(
        'recon-batch-files',
        selectedFile.name,
        selectedFile,
        'uploads'
      );

      if (!uploadResponse.data) {
        throw new Error('File upload failed - no file ID returned');
      }

      const uploadedFileId = uploadResponse.data;
      handleCloseDrawer();

      await showAlert({
        title: 'Upload complete',
        text:
          `Your file has been uploaded (ref: ${uploadedFileId}). Processing will begin shortly and transactions will appear once completed.`,
        icon: 'success',
      });

    } catch (error) {
      console.error('Error uploading file:', error);
      await showAlert({
        title: 'Upload failed',
        text: 'Something went wrong uploading the file. Please try again.',
        icon: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={activeMenuItem}
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Finance.ReconTransaction.View"
    >
      <Card
        title={`Manage Recons - ${viewLabel}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
            <Button type="button" variant="primary" onClick={handleOpenDrawer}>
              Upload
            </Button>
          </div>
        }
      >
        <div className='mb-4'>
          <GeneralSearch onSubmit={handleSearch} searchFilters={searchFilters} setSearchFilters={setSearchFilters} />
        </div>
        <ReconTransactionTable
          data={items}
          handleViewMember={handleViewMember}
          handleEditMember={handleEditMember}
          handleCreateInvoice={handleCreateInvoice}
          handleViewInvoiceHistory={handleOpenInvoiceHistoryDrawer}
          handleViewMemberBatches={handleOpenMemberBatchesDrawer}
          handleSetIsUnallocated={handleSetIsUnallocated}
          handleSetComponentId={handleSetComponentId}
          handleAssignMember={handleOpenAssignMemberDrawer}
          handleIgnoreTransaction={handleIgnoreTransaction}
          showConfirmComponentActions={viewFromUrl === 'confirmations'}
          showInvoiceHistoryAction={true}
          showMemberBatchesAction={true}
          showAssignMemberAction={viewFromUrl === 'confirmations'}
          showIgnoreAction={viewFromUrl === 'confirmations'}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
        />
      </Card>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title="Upload Recon Transactions (CSV or Excel)"
        size="small"
      >
        <div className="flex flex-col gap-4">
          <FileUpload
            id="file-upload"
            onChange={handleFileChange}
            title="Upload Bank Statement or Debit Order File"
            accept={['.csv', '.xlsx', '.xls']}
            required={true}
            allowMultiple={false}
          />
          <div className="flex gap-2 mt-2 justify-end">
            <Button
              onClick={handleCloseDrawer}
              variant="secondary"
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="primary"
              isLoading={isUploading}
              disabled={isUploading || !selectedFile}
            >
              {isUploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={isAssignMemberDrawerOpen}
        onClose={handleCloseAssignMemberDrawer}
        title="Assign Member"
        size="small"
      >
        <div className="flex flex-col gap-4">
          {selectedReconTransaction && (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <div>
                <span className="font-medium">Amount:</span>{' '}
                {selectedReconTransaction.amount != null
                  ? `R ${selectedReconTransaction.amount.toFixed(2)}`
                  : 'N/A'}
              </div>
              {selectedReconTransaction.description && (
                <div className="mt-1">
                  <span className="font-medium">Description:</span>{' '}
                  {selectedReconTransaction.description}
                </div>
              )}
            </div>
          )}

          <FormField
            id="reconMemberSearch"
            label="Member"
            type="dropdown"
            value={selectedMemberId}
            onChange={handleAssignMemberChange}
            options={memberOptions}
            placeholder="Type to search members..."
            helper="Type at least 3 characters to search"
            isLoading={isLoadingMembers}
            searchable
            onSearch={handleMemberSearch}
            searchDebounceMs={300}
          />

          <div className="flex gap-2 mt-2 justify-end">
            <Button
              onClick={handleCloseAssignMemberDrawer}
              variant="secondary"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignedMember}
              variant="primary"
              isLoading={isLoading}
              disabled={isLoading || !selectedReconTransaction?.id}
            >
              Save
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={isInvoiceHistoryDrawerOpen}
        onClose={handleCloseInvoiceHistoryDrawer}
        title="Invoice History"
        size="large"
      >
        {invoiceHistoryMemberId ? (
          <ViewInvoiceHistory key={invoiceHistoryMemberId} memberId={invoiceHistoryMemberId} />
        ) : null}
      </Drawer>

      <Drawer
        isOpen={isMemberBatchesDrawerOpen}
        onClose={handleCloseMemberBatchesDrawer}
        title="Member Batches"
        size="large"
      >
        {memberBatchesMemberId ? (
          <ViewMemberBatches
            memberId={memberBatchesMemberId}
            idPassportNumber={memberBatchesIdPassportNumber}
          />
        ) : null}
      </Drawer>
    </AdminLayout>
  );
};

export default ReconTransactionList;
